import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { Stack, ThemeProvider } from '@mui/material';
import AdminOptions from '~/pages/AdminOptions';
import { TaskList } from '~/pages/TaskList';
import { PaymentProvider, usePaymentInfo } from '~/providers/PaymentProvider';
import { DialogUpSellUpgradePlan } from '~/components/shared/UpgradePlan/DialogUpgradePlan';
import Dashboard from '~/pages/Dashboard';
import { FailPaymentController } from '~/components/shared/Subscriptions/FailPaymentController';
import { AddressBook } from '~/pages/AddressBook';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  AppLayout,
  AnnouncementProvider,
  Announcement,
  theme as unifiedTheme,
  NotificationProvider,
  NotificationType,
} from 'bs-unified-ui';
import {
  UnifiedNavbar,
  CS_USER_ROLES,
} from '~/layout/UnifiedNavbar/UnifiedNavbar';
import { LiveTracking } from '~/pages/LiveTracking';
import { GoogleMapWrapper } from '~/providers/GoogleMapWrapper';
import CreateWorkspace from '~/pages/CreateWorkspace';
import { memoryStorage } from '~/utils/localStorage';
import { COM_HUB_API_ENDPOINT, NOTIFICATION_ENDPOINTS } from '~/config';
import { firebaseGetToken } from '~/services/firebase';
import { useAuth } from '~/providers/AuthProvider';
import {
  useGetCSNotificationSound,
  useGetBSNotificationSetting,
  useGetCFNotificationSetting,
} from '~/services/api/shared';
import { TIMEZONE_SETTING } from '~/constants/adminOptions';
import momentTz from 'moment-timezone';
import { DEFAULT_TIMEZONE } from '~/constants/common';
import { DEFAULT_UNIFIED_NOTIFICATION_SOUND } from '~/constants/auth';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCF1997Enabled = useFeatureIsOn('cf-1997-communication-hub');
  const isNotiV2 = useFeatureIsOn('cs_9035_notification');

  const { org: orgInfo } = usePaymentInfo();
  const { account, unifiedNavigation, storeId } = useAuth();

  const csWsId = useMemo(
    () => unifiedNavigation?.org?.cs_ws_id,
    [unifiedNavigation.org],
  );

  const { data: csNotificationData, refetch: refetchCsNotificationSetting } =
    useGetCSNotificationSound({
      enabled: !!orgInfo?.bs_org_id,
    });

  const currentCsWorkSpace = useMemo(
    () =>
      csNotificationData?.workspaces?.find(
        (ws) => ws?.workspaceId?.id === csWsId,
      ),
    [csNotificationData, csWsId],
  );

  const { data: bsNotificationData, refetch: refetchBsNotificationSetting } =
    useGetBSNotificationSetting({
      enabled: !!orgInfo?.bs_org_id && !!storeId,
      params: {
        organization_id: orgInfo?.bs_org_id,
        store_id: storeId,
        parameters: {
          store_ids: [storeId],
        },
      },
    });

  const {
    data: cfNotificationSettings,
    refetch: refetchCfNotificationSetting,
  } = useGetCFNotificationSetting({
    enabled: !!orgInfo?.id,
    params: orgInfo?.id,
  });

  const timezone = useMemo(() => {
    if (account.timezone) {
      if (account.timezone === TIMEZONE_SETTING.AUTO_DETECT) {
        let tz = momentTz.tz.guess();
        if (tz === 'Asia/Saigon') {
          tz = 'Asia/Ho_Chi_Minh';
        }
        return tz;
      }
      return account.timezone;
    }
    return DEFAULT_TIMEZONE;
  }, [account.timezone]);

  const canAccessLiveChat = useMemo(() => {
    if (currentCsWorkSpace?.role === 'custom_role')
      return currentCsWorkSpace?.customRoles?.chatso_live_chat;

    return [
      CS_USER_ROLES.ADMIN,
      CS_USER_ROLES.CHATSO_ADMIN,
      CS_USER_ROLES.AGENT,
    ].includes(currentCsWorkSpace?.role);
  }, [currentCsWorkSpace]);

  const canAccessOrder = useMemo(() => {
    if (currentCsWorkSpace?.role === 'custom_role')
      return currentCsWorkSpace?.customRoles?.breadstack_order;

    return [
      CS_USER_ROLES.ADMIN,
      CS_USER_ROLES.CHATSO_ADMIN,
      CS_USER_ROLES.AGENT,
      CS_USER_ROLES.MARKETING_MANAGER,
      CS_USER_ROLES.MARKETING_AGENT,
    ].includes(currentCsWorkSpace?.role);
  }, [currentCsWorkSpace]);

  const handleNotification = useCallback(
    (notification) => {
      if (notification?.event === NotificationType.SYNC_SETTINGS) {
        if (orgInfo?.bs_org_id) {
          refetchBsNotificationSetting();
          refetchCsNotificationSetting();
        }
        refetchCfNotificationSetting();
      }
    },
    [
      refetchBsNotificationSetting,
      refetchCsNotificationSetting,
      refetchCfNotificationSetting,
      orgInfo?.bs_org_id,
    ],
  );

  const soundConfig = useMemo(() => {
    if (isNotiV2)
      return {
        ...(csNotificationData?.config?.chatNotificationSound?.soundConfig ||
          {}),
        delivery: {
          mute: cfNotificationSettings?.mute,
          url: cfNotificationSettings?.url,
          repeatingInterval: cfNotificationSettings?.repeatingInterval,
        },
      };

    return (
      csNotificationData?.config?.chatNotificationSound?.soundConfig ||
      DEFAULT_UNIFIED_NOTIFICATION_SOUND
    );
  }, [
    csNotificationData?.config?.chatNotificationSound?.soundConfig,
    cfNotificationSettings?.mute,
    cfNotificationSettings?.url,
    cfNotificationSettings?.repeatingInterval,
    isNotiV2,
  ]);

  const warehousesConfig = useMemo(() => {
    const listConfig = bsNotificationData?.data?.warehouses?.reduce(
      (acc, w) => {
        if ('all' in w) {
          if (w?.all) return [...acc, 'all'];
          return acc;
        }
        return [...acc, w?.warehouse_id];
      },
      [],
    );
    return listConfig;
  }, [bsNotificationData?.data?.warehouses]);

  const notificationConfig = useMemo(
    () => ({
      apiBaseUrl: NOTIFICATION_ENDPOINTS,
      streamEndpoint: '/notifications/stream',
      notificationEndpoint: '/notifications',
      auth: {
        getFirebaseToken: firebaseGetToken,
        sessionTokenEndpoint: '/notifications/stream/token',
      },
      timezone,
      navigate,
      soundConfig,
      isV2: isNotiV2,
      ...(orgInfo?.bs_org_id
        ? {
            onNotification: handleNotification,
            bsOrgId: orgInfo?.bs_org_id,
            warehousesConfig,
            triggerEvents: {
              new_pending_chat:
                canAccessLiveChat &&
                csNotificationData?.config?.chatNotificationSound?.pendingChat
                  ?.enabled,
              new_unread_message:
                canAccessLiveChat &&
                csNotificationData?.config?.chatNotificationSound?.newMessage
                  ?.enabled,
              customer_left:
                canAccessLiveChat &&
                csNotificationData?.config?.chatNotificationSound?.customerLeft
                  ?.enabled,
              missed_chat:
                canAccessLiveChat &&
                csNotificationData?.config?.chatNotificationSound?.missedChat
                  ?.enabled,
              new_incoming_order:
                canAccessOrder &&
                bsNotificationData?.data?.[storeId]?.new_order,
              cancelled_order:
                canAccessOrder &&
                bsNotificationData?.data?.[storeId]?.order_cancelled,

              task_created: cfNotificationSettings?.task_update?.enable,
              task_completed: cfNotificationSettings?.task_update?.enable,
              task_failed: cfNotificationSettings?.task_update?.enable,
              failed_to_pick_up: cfNotificationSettings?.task_update?.enable,
              success_to_pick_up: cfNotificationSettings?.task_update?.enable,
              failed_to_return: cfNotificationSettings?.task_update?.enable,
              success_to_return: cfNotificationSettings?.task_update?.enable,
              import_task: cfNotificationSettings?.import_csv?.enable,
              new_driver: cfNotificationSettings?.new_driver?.enable,
            },
          }
        : {
            onNotification: handleNotification,
            cfWsId: orgInfo?.id,
            triggerEvents: {
              task_created: cfNotificationSettings?.task_update?.enable,
              task_completed: cfNotificationSettings?.task_update?.enable,
              task_failed: cfNotificationSettings?.task_update?.enable,
              failed_to_pick_up: cfNotificationSettings?.task_update?.enable,
              success_to_pick_up: cfNotificationSettings?.task_update?.enable,
              failed_to_return: cfNotificationSettings?.task_update?.enable,
              success_to_return: cfNotificationSettings?.task_update?.enable,
              import_task: cfNotificationSettings?.import_csv?.enable,
              new_driver: cfNotificationSettings?.new_driver?.enable,
            },
          }),
    }),
    [
      timezone,
      navigate,
      orgInfo?.bs_org_id,
      orgInfo?.id,
      bsNotificationData,
      cfNotificationSettings,
      csNotificationData,
      handleNotification,
      canAccessLiveChat,
      canAccessOrder,
      storeId,
      soundConfig,
      warehousesConfig,
    ],
  );

  const announcementConfig = useMemo(
    () => ({
      apiUrl: COM_HUB_API_ENDPOINT,
      enabled: isCF1997Enabled,
      getFirebaseToken: firebaseGetToken,
      ...(orgInfo?.bs_org_id && { orgId: orgInfo.bs_org_id }),
    }),
    [orgInfo?.bs_org_id, isCF1997Enabled],
  );

  return (
    <AnnouncementProvider {...announcementConfig}>
      <ThemeProvider theme={unifiedTheme}>
        <Announcement renderGlobalBanner renderPopup />
      </ThemeProvider>
      <NotificationProvider config={notificationConfig}>
        <AppLayout
          renderSideBar={
            location.pathname === '/create-workspace' ? '' : <UnifiedNavbar />
          }
        >
          <DialogUpSellUpgradePlan />
          <FailPaymentController />
          <Outlet />
        </AppLayout>
      </NotificationProvider>
    </AnnouncementProvider>
  );
};

const App = () => (
  <Stack display='flex' flexDirection='column' height='100%'>
    <PaymentProvider>
      <GoogleMapWrapper>
        <AppContent />
      </GoogleMapWrapper>
    </PaymentProvider>
  </Stack>
);

const WorkspaceWrapper = () => {
  const { id, '*': splat } = useParams();
  const location = useLocation();

  if (splat === '' && !location.pathname.endsWith('/')) {
    return <Navigate to={`/ws/${id}/`} replace />;
  }

  return <Outlet context={{ workspaceId: id }} />;
};

const RedirectToWorkspace = () => {
  const workspaceId = memoryStorage.getOrgId();
  if (workspaceId) return <Navigate to={`/ws/${workspaceId}/`} replace />;
  return <Navigate to='/ws/' replace />;
};

export const protectedRoutes = () => {
  const routers = [
    {
      path: '/ws/:id/*',
      element: <WorkspaceWrapper />,
      children: [
        {
          path: '',
          element: <App />,
          children: [
            {
              path: '',
              element: <LiveTracking />,
            },
            {
              path: 'insight',
              element: <Dashboard />,
            },
            {
              path: 'tasks',
              element: <TaskList />,
            },
            {
              path: 'address',
              element: <AddressBook />,
            },
            {
              path: 'setting',
              element: <AdminOptions />,
              children: [{ path: ':source', element: <AdminOptions /> }],
            },
            { path: '*', element: <Navigate to='' replace /> },
          ],
        },
      ],
    },
    {
      path: '/create-workspace',
      element: <App />,
      children: [
        {
          path: '',
          element: <CreateWorkspace />,
        },
      ],
    },
    { path: '*', element: <RedirectToWorkspace /> },
  ];

  return routers;
};
